import React, { useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Button, Tooltip, Upload, message, MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { ReactComponent as IconHB } from '@svg/common/header-hb.svg';
import { ReactComponent as IconNavHome } from '@svg/common/nav-home.svg';
import { ReactComponent as IconNavVideo } from '@svg/common/nav-video.svg';
// import { ReactComponent as IconNavStudio } from '@svg/common/nav-studio.svg';
import { ReactComponent as IconEdit } from '@svg/icon-edit.svg';
import { scVarTransitionDuration } from '@utils/emotion/variables';
import { scMixinEllipsis, scMixinAbsoluteFull } from '@utils/emotion/mixins';

import { useUIStore } from '@stores/ui';
import { UploaderContext } from '~/lib/video-uploader';
import SiderSocialLinks from '@components/SiderSocialLinks';
import { useAuthStore } from '@stores/auth';

const { Sider: AntdSider } = Layout;

interface Props {
  hidden: boolean;
}

const ContainerSider: React.FC<Props> = ({ hidden = false }) => {
  const { t } = useTranslation();
  const { userData } = useAuthStore();
  const { isSiderCollapsed, toggleSider, showUserModal } = useUIStore();
  const location = useLocation();
  const navigate = useNavigate();
  const videoUploader = useContext(UploaderContext);
  const userPictureRef = useRef<HTMLImageElement | null>(null);

  const onClickMenuItem: MenuProps['onClick'] = (e) => {
    navigate(e.key);
  };

  if (hidden) {
    return <></>;
  }

  return (
    <Sider
      collapsible
      collapsed={isSiderCollapsed}
      onCollapse={toggleSider}
      trigger={null}
      width={240}
    >
      <div className="minimal-area">
        <Tooltip
          placement="right"
          title={t(`nav.tooltip.menu.${isSiderCollapsed ? 'expand' : 'fold'}`)}
        >
          <button
            className="btn-collapse"
            onClick={() => {
              toggleSider(!isSiderCollapsed);
            }}
            title={t(`nav.tooltip.menu.${isSiderCollapsed ? 'expand' : 'fold'}`)}
          >
            <IconHB />
          </button>
        </Tooltip>
      </div>
      <div className="sider-header">
        <div className="thumbnail-area">
          <Tooltip placement={isSiderCollapsed ? 'right' : 'top'} title={t('sider.profile.edit')}>
            <a
              href="#profile"
              role="button"
              className="thumbnail"
              onClick={(e) => {
                e.preventDefault();
                showUserModal();
              }}
            >
              <div className="frame">
                <img
                  className="image"
                  src={userData?.avatarUrl ?? '/assets/common/userpicture-default-md.png'}
                  alt={userData?.name}
                  ref={userPictureRef}
                  referrerPolicy="no-referrer"
                  onError={() => {
                    userPictureRef.current!.src = '/assets/common/userpicture-default-md.png';
                  }}
                />
              </div>
              <div className="hover-dimmer">
                <i className="icon">
                  <IconEdit width="24" height="24" />
                </i>
              </div>
            </a>
          </Tooltip>
        </div>
        <div className="info-area">
          <strong className="user-name">{userData?.name}</strong>
        </div>
        {userData?.creator && (
          <div className="action-area">
            <Upload
              name="video"
              multiple
              showUploadList={false}
              beforeUpload={(file) => {
                if (file.name.endsWith('.mkv') && file.type === '') return true;
                switch (file.type) {
                  case 'video/mp4':
                  case 'video/webm':
                  case '.mkv':
                  case 'video/quicktime':
                  case 'video/x-msvideo':
                  case 'video/x-flv':
                    return true;
                  default:
                    message.error(t('upload.error.format'));
                    return false;
                }
              }}
              customRequest={(options) => videoUploader.createVideoUpload(options)}
            >
              <Button type="primary">{t('nav.main.new')}</Button>
            </Upload>
          </div>
        )}
      </div>
      <Menu
        mode="inline"
        defaultSelectedKeys={['/']}
        selectedKeys={[location.pathname]}
        onClick={onClickMenuItem}
        items={[
          {
            key: '/',
            label: t('nav.main.home'),
            icon: (
              <span className="icon">
                <IconNavHome />
              </span>
            ),
          },
          {
            key: '/videos',
            label: t('nav.main.videos'),
            icon: (
              <span className="icon">
                <IconNavVideo />
              </span>
            ),
          },
        ]}
      />
      <div className="sider-social">
        <SiderSocialLinks />
      </div>
      <div className="sider-footer">
        <a href="https://www.devunlimit.com" target="_blank" rel="noreferrer noopener">
          <i className="copyright">ⓒ DEVUNLIMIT Inc.</i>
        </a>
      </div>
    </Sider>
  );
};

const Sider = styled(AntdSider)`
  overflow: hidden;
  flex: 0 0 240px !important;
  position: relative;
  z-index: 1;
  width: 240px !important;
  min-width: 240px !important;
  max-width: 240px !important;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);

  &.ant-layout-sider-collapsed {
    flex: 0 0 62px !important;
    width: 62px !important;
    min-width: 62px !important;
    max-width: 62px !important;

    .ant-layout-sider-children {
      padding: 118px 0 0 62px;
    }

    .minimal-area {
      background-color: ${({ theme }) => theme.sidebar.minimalBgCollapsedColor};
    }

    .ant-menu {
      width: auto;

      .ant-menu-item {
        strong {
          opacity: 0;
        }
      }
    }

    .sider-header {
      top: 64px;
      left: 0;
      width: 62px;

      .thumbnail-area {
        width: 62px;

        .thumbnail .frame .image {
          width: 28px;
          height: 28px;
        }

        .hover-dimmer .icon svg {
          width: 12px;
          height: 12px;
        }
      }

      .info-area,
      .action-area {
        display: none;
      }
    }

    .sider-social,
    .sider-footer {
      display: none;
    }
  }

  .minimal-area {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    width: 62px;
    background-color: ${({ theme }) => theme.sidebar.minimalBgColor};

    .btn-collapse,
    .btn-back {
      path {
        fill: ${({ theme }) => theme.colors.fontSubColor};
      }
    }

    .btn-collapse {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 56px;
      transition: opacity 400ms ease;
      cursor: pointer;

      &:hover {
        opacity: 0.5;
      }
    }
  }

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    padding: 259px 0 0 62px;
    background-color: ${({ theme }) => theme.sidebar.bgColor};
    color: ${({ theme }) => theme.colors.fontColor};
  }

  .ant-menu {
    flex: 1;
    width: auto;
    margin: 0 0 0 -62px;
    border-right: none;
    background-color: ${({ theme }) => theme.sidebar.bgColor};
    transition: none;

    .ant-menu-item {
      overflow: visible;
      position: relative;
      margin: 0 !important;
      padding: 0 0 0 62px !important;

      &::after {
        display: none !important;
      }

      &:active {
        background: transparent;
      }

      &.ant-menu-item-selected {
        background-color: ${({ theme }) => theme.colors.primary.bgColor};

        .ant-menu-item-icon svg path {
          fill: ${({ theme }) => theme.colors.primary.iconSelectedColor};
        }
      }

      &.ant-menu-item-disabled {
        opacity: 0.5;
      }

      & + .ant-menu-item {
        margin-top: 4px !important;
      }

      .icon {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        width: 62px;
        height: 40px;

        .anticon {
          margin-right: 0 !important;
        }

        svg {
          display: block;
          width: 24px;
          height: 24px;

          path {
            fill: ${({ theme }) => theme.colors.primary.iconColor};
          }
        }
      }

      .ant-menu-title-content {
        margin: 0;
        padding: 0 12px 0 26px;
        font-size: 15px;
        line-height: 1.5;
        color: ${({ theme }) => theme.colors.fontColor};
        opacity: 1;
        transition: color ${scVarTransitionDuration} ease, opacity 0.1s ease;
      }
    }
  }

  .sider-header {
    position: absolute;
    top: 56px;
    left: 62px;
    width: 178px;
    transition: top 0.2s ease, left 0.2s ease;

    .thumbnail-area {
      display: flex;
      justify-content: center;
    }

    .thumbnail {
      overflow: hidden;
      position: relative;
      border-radius: 50%;
      outline: none;
      cursor: pointer;

      &::before {
        display: block;
        ${scMixinAbsoluteFull};
        border: 2px solid rgba(0, 0, 0, 0.05);
        border-radius: 50%;
        content: '';
      }

      &:hover,
      &:focus {
        .hover-dimmer {
          opacity: 1;
        }
      }

      .image {
        display: block;
        overflow: hidden;
        width: 92px;
        height: 92px;
        border-radius: 50%;
        transition: width 0.2s ease, height 0.2s ease;

        &.is-empty {
          background-color: #eee;
        }
      }
    }

    .hover-dimmer {
      display: flex;
      justify-content: center;
      align-items: center;
      ${scMixinAbsoluteFull};
      background-color: rgba(9, 9, 9, 0.8);
      opacity: 0;
      transition: opacity 200ms ease;

      .icon {
        display: block;
        line-height: 0;

        svg {
          vertical-align: top;

          path {
            fill: #fff;
          }
        }
      }
    }

    .info-area {
      margin-top: 12px;
      padding: 0 16px;
      text-align: center;
      color: ${({ theme }) => theme.colors.fontSubColor};
      transition: color ${scVarTransitionDuration} ease;

      .user-name {
        ${scMixinEllipsis};
        display: block;
        font-size: 14px;
        line-height: 1.5;
      }
    }

    .action-area {
      margin-top: 12px;
      text-align: center;

      .ant-btn {
        color: #fff;
      }
    }
  }

  .sider-social {
    width: 178px;
    padding: 24px;
    border-top: 1px solid ${({ theme }) => theme.sidebar.dividerColor};
  }

  .sider-footer {
    min-width: 194px;
    padding: 24px 0 16px 24px;
    border-top: 1px solid ${({ theme }) => theme.sidebar.dividerColor};
    line-height: 16px;

    a {
      display: inline-block;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
        text-decoration-color: ${({ theme }) => theme.colors.fontColor};
      }
    }

    .copyright {
      font-family: 'Roboto', Arial, Helvetica, sans-serif;
      font-size: 13px;
      font-style: normal;
      line-height: 1.2;
      color: ${({ theme }) => theme.colors.fontColor};
    }
  }
`;

export default ContainerSider;
